/**
 * Conditional Authentication Provider Module
 * Provides authentication that switches between session-based auth and API key auth
 * based on URL parameters for embedding functionality
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom'
import { SessionProvider } from './session-provider'
import { ApiKeyProvider, useApiKeyAuth } from './api-key-provider'
import { EmbedKeyProvider, useEmbedKeyAuth } from './embed-key-provider'
import { PostMessageAuthProvider, usePostMessageAuth } from './postmessage-auth-provider'
import { AgentAccessRequired } from '@/components/agent-access-required'
import { useAuthStore } from '@/store/slices/auth-slice'
import { isTokenValid, isRefreshTokenValid } from '@/utils/auth-storage'

/**
 * Props for the ConditionalAuthProvider component
 * @interface ConditionalAuthProviderProps
 * @property {ReactNode} children - Child components to render with authentication context
 */
interface ConditionalAuthProviderProps {
  children: ReactNode
}

/**
 * Context for conditional authentication state and configuration
 * @interface ConditionalAuthContext
 * @property {boolean} isEmbedMode - Whether the component is running in embed mode
 * @property {boolean} isBubbleMode - Whether the component is in bubble chat mode (no history)
 * @property {string | null} apiKey - The API key used for authentication in embed mode
 * @property {boolean} isAuthenticated - Whether authentication was successful
 * @property {string | null} authMode - Current authentication mode ('session' | 'apikey' | 'embedkey' | 'postmessage' | null)
 */
interface ConditionalAuthContext {
  isEmbedMode: boolean
  isBubbleMode: boolean
  apiKey: string | null
  isAuthenticated: boolean
  authMode: 'session' | 'apikey' | 'embedkey' | 'postmessage' | null
}

/**
 * Default context values for conditional authentication
 * Used when the context is accessed outside of a provider
 */
const defaultContextValue: ConditionalAuthContext = {
  isEmbedMode: false,
  isBubbleMode: false,
  apiKey: null,
  isAuthenticated: false,
  authMode: null
}

/**
 * React context for conditional authentication state
 * Provides authentication state and configuration to child components
 */
const ConditionalAuthContext = createContext<ConditionalAuthContext>(defaultContextValue)

/**
 * Hook to access conditional authentication context
 * @returns {ConditionalAuthContext} The current authentication context state
 * @throws {Error} When used outside of ConditionalAuthProvider
 */
export function useConditionalAuth(): ConditionalAuthContext {
  const context = useContext(ConditionalAuthContext)

  if (!context) {
    throw new Error('useConditionalAuth must be used within a ConditionalAuthProvider')
  }

  return context
}

/**
 * Auth State Wrapper Component
 * Wraps children with authentication state from the appropriate provider
 * Updates the ConditionalAuthContext with authentication status
 *
 * @param {ConditionalAuthProviderProps & { authMode: string, isEmbedMode: boolean, isBubbleMode: boolean, apiKey: string | null }} props - Component props with auth config
 * @returns {JSX.Element} Wrapper component that tracks authentication state
 */
function AuthStateWrapper({
  children,
  authMode,
  isEmbedMode,
  isBubbleMode,
  apiKey
}: ConditionalAuthProviderProps & {
  authMode: 'session' | 'apikey' | 'embedkey' | 'postmessage' | null
  isEmbedMode: boolean
  isBubbleMode: boolean
  apiKey: string | null
}) {
  /**
   * Authentication state from the appropriate provider
   * Dynamically tracks authentication status
   */
  let isAuthenticated = false

  // Get authentication status from the appropriate provider
  if (authMode === 'apikey') {
    // In API key mode, get authentication status from ApiKeyProvider
    try {
      const apiKeyAuth = useApiKeyAuth()
      isAuthenticated = apiKeyAuth.isAuthenticated
    } catch {
      // Provider not available yet, default to false
      isAuthenticated = false
    }
  } else if (authMode === 'embedkey') {
    // In embed key mode, get authentication status from EmbedKeyProvider
    try {
      const embedKeyAuth = useEmbedKeyAuth()
      isAuthenticated = embedKeyAuth.isAuthenticated
    } catch {
      // Provider not available yet, default to false
      isAuthenticated = false
    }
  } else if (authMode === 'session') {
    // In session mode, get authentication status from auth store
    try {
      const authStore = useAuthStore()
      isAuthenticated = authStore.isAuthenticated
    } catch {
      // Provider not available yet, default to false
      isAuthenticated = false
    }
  } else if (authMode === 'postmessage') {
    // In postMessage mode, get authentication status from PostMessageAuthProvider
    try {
      const postMessageAuth = usePostMessageAuth()
      isAuthenticated = postMessageAuth.isAuthenticated
    } catch {
      // Provider not available yet, default to false
      isAuthenticated = false
    }
  }

  /**
   * Context value with dynamic authentication state
   * Updates based on active authentication provider
   */
  const contextValue: ConditionalAuthContext = {
    isEmbedMode,
    isBubbleMode,
    apiKey,
    isAuthenticated,
    authMode
  }

  return (
    <ConditionalAuthContext.Provider value={contextValue}>
      {children}
    </ConditionalAuthContext.Provider>
  )
}

/**
 * Conditional Authentication Provider Component
 * Provides authentication that switches between session-based and API key authentication
 * based on URL parameters for embedding functionality
 *
 * Authentication Modes:
 * - session: Standard session-based authentication for authenticated users
 * - apikey: API key authentication for embedded agent chats
 *
 * @param {ConditionalAuthProviderProps} props - Component props
 * @returns {JSX.Element} Provider component with authentication logic
 */
export function ConditionalAuthProvider({ children }: ConditionalAuthProviderProps) {
  /**
   * URL search parameters for detecting embed mode and API key
   * Used to determine authentication strategy
   */
  const [searchParams] = useSearchParams()
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  /**
   * Whether the component is running in embed mode
   * Determined by presence of 'embed=true' parameter
   */
  const [isEmbedMode, setIsEmbedMode] = useState(false)

  /**
   * Whether the component is in bubble chat mode
   * Determined by presence of 'bubble=true' parameter
   */
  const [isBubbleMode, setIsBubbleMode] = useState(false)

  /**
   * API key from URL parameters
   * Used for authentication in embed mode
   */
  const [apiKey, setApiKey] = useState<string | null>(null)

  /**
   * Public embed key from URL parameters (pk_embed_...).
   * Preferred secure embed path: exchanged server-side for a Bearer token so
   * the secret API key never reaches the browser.
   */
  const [embedKey, setEmbedKey] = useState<string | null>(null)

  /**
   * Current authentication mode being used
   * Either 'session', 'apikey', 'embedkey', 'postmessage', or null if not authenticated
   */
  const [authMode, setAuthMode] = useState<'session' | 'apikey' | 'embedkey' | 'postmessage' | null>(null)

  /**
   * Effect to parse URL parameters and determine authentication mode
   * Runs when search parameters change
   */
  useEffect(() => {
    // Check for embed mode parameter
    const embedParam = searchParams.get('embed')
    const isEmbed = embedParam === 'true'
    setIsEmbedMode(isEmbed)

    // Check for bubble mode parameter
    const bubbleParam = searchParams.get('bubble')
    const isBubble = bubbleParam === 'true'
    setIsBubbleMode(isBubble)

    // Extract API key parameter (legacy secret-key path)
    const keyParam = searchParams.get('apikey')
    setApiKey(keyParam)

    // Extract public embed key parameter (preferred secure path)
    const embedKeyParam = searchParams.get('embedKey')
    setEmbedKey(embedKeyParam)

    // Check for postMessage auth mode (secure embed without key in URL)
    const postMessageParam = searchParams.get('postMessage')
    const usePostMessage = postMessageParam === 'true'

    // Check for any valid session (both admin and end user)
    const hasValidSession = isTokenValid()

    // Determine authentication mode based on parameters and session
    if (isEmbed && usePostMessage) {
      // Embed mode with postMessage - use secure postMessage authentication
      setAuthMode('postmessage')
    } else if (isEmbed && embedKeyParam) {
      // Embed mode with PUBLIC embed key - exchange server-side (secure, preferred)
      setAuthMode('embedkey')
    } else if (isEmbed && keyParam) {
      // Embed mode with secret API key in URL - DEPRECATED legacy path, retained
      // for back-compat with live embeds generated before #1024.
      // A secret in a URL leaks via browser history, referrers, and pasted
      // snippets — surface the deprecation loudly so integrators migrate.
      console.warn(
        '[Taqat Embed] DEPRECATED: authenticating with a secret API key in the URL (?apikey=). ' +
        'Regenerate your embed snippet to use the public embed key (pk_embed_...) — ' +
        'this legacy path will be removed in a future release.'
      )
      setAuthMode('apikey')
    } else if (hasValidSession) {
      // Valid session exists - use session authentication
      setAuthMode('session')
    } else if (!isEmbed) {
      // Normal mode without session - SessionProvider will redirect to /signin if needed
      setAuthMode('session')
    } else {
      // Embed mode without API key or postMessage - invalid configuration
      setAuthMode(null)
    }
  }, [searchParams, agentId, navigate])

  // Anonymous visitor to a standalone agent page (/agent/:id/...): show a stable
  // "sign in to continue" screen instead of handing off to SessionProvider, which
  // redirects to /signin. On a custom agent domain that redirect ping-pongs with
  // the catch-all (* → /agent/:id/chat/new) and produces a flickering reload loop.
  // Read `embed` straight from the URL so this never flashes on first render for
  // embed deep-links (isEmbedMode state is only set after the effect runs).
  const isEmbedRequest = searchParams.get('embed') === 'true'
  const isStandaloneAgentRoute = location.pathname.startsWith('/agent/')
  const hasNoSession = !isTokenValid() && !isRefreshTokenValid()
  if (!isEmbedRequest && isStandaloneAgentRoute && hasNoSession) {
    return (
      <ConditionalAuthContext.Provider
        value={{
          isEmbedMode: false,
          isBubbleMode: false,
          apiKey: null,
          isAuthenticated: false,
          authMode: 'session',
        }}
      >
        <AgentAccessRequired mode="signin" />
      </ConditionalAuthContext.Provider>
    )
  }

  // If embed mode but no key/postMessage auth, show error
  if (isEmbedMode && !apiKey && !embedKey && authMode !== 'postmessage') {
    const errorContextValue: ConditionalAuthContext = {
      isEmbedMode,
      isBubbleMode,
      apiKey,
      isAuthenticated: false,
      authMode
    }

    return (
      <ConditionalAuthContext.Provider value={errorContextValue}>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center p-6 max-w-md">
            <h2 className="text-lg font-semibold mb-2 text-destructive">
              Invalid Embed Configuration
            </h2>
            <p className="text-muted-foreground">
              Embed mode requires authentication. Please use postMessage auth or include an 'embedKey' parameter.
            </p>
          </div>
        </div>
      </ConditionalAuthContext.Provider>
    )
  }

  // Render appropriate authentication provider based on mode
  if (authMode === 'embedkey' && embedKey) {
    // Embed mode with PUBLIC embed key - exchange server-side (secure, preferred)
    return (
      <EmbedKeyProvider embedKey={embedKey}>
        <AuthStateWrapper
          authMode={authMode}
          isEmbedMode={isEmbedMode}
          isBubbleMode={isBubbleMode}
          apiKey={apiKey}
        >
          {children}
        </AuthStateWrapper>
      </EmbedKeyProvider>
    )
  } else if (authMode === 'postmessage') {
    // Embed mode with postMessage - use secure postMessage authentication
    return (
      <PostMessageAuthProvider>
        <AuthStateWrapper
          authMode={authMode}
          isEmbedMode={isEmbedMode}
          isBubbleMode={isBubbleMode}
          apiKey={apiKey}
        >
          {children}
        </AuthStateWrapper>
      </PostMessageAuthProvider>
    )
  } else if (authMode === 'apikey' && apiKey) {
    // Embed mode with API key in URL - use API key authentication (legacy)
    return (
      <ApiKeyProvider apiKey={apiKey}>
        <AuthStateWrapper
          authMode={authMode}
          isEmbedMode={isEmbedMode}
          isBubbleMode={isBubbleMode}
          apiKey={apiKey}
        >
          {children}
        </AuthStateWrapper>
      </ApiKeyProvider>
    )
  } else if (authMode === 'session') {
    // Normal mode - use session authentication
    return (
      <SessionProvider>
        <AuthStateWrapper
          authMode={authMode}
          isEmbedMode={isEmbedMode}
          isBubbleMode={isBubbleMode}
          apiKey={apiKey}
        >
          {children}
        </AuthStateWrapper>
      </SessionProvider>
    )
  }

  // Fallback - show loading while redirecting or if auth mode not yet determined
  const fallbackContextValue: ConditionalAuthContext = {
    isEmbedMode,
    isBubbleMode,
    apiKey,
    isAuthenticated: false,
    authMode
  }

  // While auth is still being determined, show a loading state
  if (agentId) {
    return (
      <ConditionalAuthContext.Provider value={fallbackContextValue}>
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground">Redirecting to login...</p>
          </div>
        </div>
      </ConditionalAuthContext.Provider>
    )
  }

  return (
    <ConditionalAuthContext.Provider value={fallbackContextValue}>
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-destructive">
            Authentication Error
          </h2>
          <p className="text-muted-foreground">
            Unable to determine authentication method. Please check your configuration.
          </p>
        </div>
      </div>
    </ConditionalAuthContext.Provider>
  )
}

ConditionalAuthProvider.displayName = 'ConditionalAuthProvider'